import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight, ArrowRight, Star, ShieldCheck, Truck, Recycle,
  Boxes, BadgeIndianRupee, Sparkles, Headphones, MapPin, Layers,
} from "lucide-react";
import { MaskLine, FadeIn, Reveal } from "@/components/Reveal";
import { CATEGORIES, WHY_CHOOSE, PROCESS, INDUSTRIES, TESTIMONIALS, GALLERY, BRAND } from "@/data/catalog";

const WHY_ICONS = [ShieldCheck, BadgeIndianRupee, Boxes, Truck, Sparkles, Layers, Recycle, MapPin, Headphones];

function Overline({ children }) {
  return <span className="overline text-royal inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald" />{children}</span>;
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden pt-28 pb-14">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute right-0 top-0 h-full w-full lg:w-[52%]">
          <img src={CATEGORIES[0].image} alt="Premium packaging" className="h-full w-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-background via-background/40 to-transparent" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 lg:px-10">
          <FadeIn delay={0.1}><Overline>Manufacturer & Wholesale Supplier · India</Overline></FadeIn>
          <h1 className="fluid-hero font-heading font-extrabold text-foreground mt-6">
            <MaskLine delay={0.15}>Premium Packaging</MaskLine>
            <MaskLine delay={0.3}>Solutions That</MaskLine>
            <MaskLine delay={0.45}><span className="text-royal">Protect</span> Your Brand</MaskLine>
          </h1>
          <FadeIn delay={0.8} className="mt-8 flex flex-col lg:flex-row lg:items-end gap-6 lg:justify-between">
            <p className="max-w-lg text-base lg:text-lg text-muted-foreground leading-relaxed">
              From courier bags to premium gift boxes, ElitzPacks delivers innovative packaging solutions for businesses of every size.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/request-quote" data-testid="hero-quote-btn" className="group inline-flex items-center gap-2 rounded-full bg-navy text-white px-7 py-3.5 font-semibold hover:bg-royal transition-colors">
                <span className="h-2 w-2 rounded-full bg-emerald animate-pulseglow" /> Get Quote
                <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
              <Link to="/products" data-testid="hero-browse-btn" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 font-semibold text-foreground hover:bg-secondary transition-colors">
                Browse Products <ArrowRight size={18} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-border py-5 bg-secondary/40">
        <Marquee speed={40} gradient={false} className="font-mono-accent text-sm uppercase tracking-[0.18em] text-muted-foreground">
          {["Corrugated Boxes", "Luxury Rigid Boxes", "Courier Bags", "Food Packaging", "Eco-Friendly Bags", "Bubble & Foam", "Custom Printing", "Bulk Manufacturing", "Fast Delivery"].map((t, i) => (
            <span key={i} className="mx-8 flex items-center gap-8">{t}<span className="text-emerald">✦</span></span>
          ))}
        </Marquee>
      </section>

      {/* ABOUT / MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal><Overline>Protect. Pack. Promote.</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-heading text-4xl lg:text-5xl font-extrabold text-foreground leading-[1.05]">
                Complete packaging solutions under one roof.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-8">
            {[
              { n: "01", t: "Protect", d: "Engineered strength and precision fit that keeps products safe through every mile of transit." },
              { n: "02", t: "Pack", d: "A vast range — flexible, rigid, food, industrial and eco — manufactured and supplied at scale." },
              { n: "03", t: "Promote", d: "Custom printing, foil and finishes that turn every package into a branded experience." },
            ].map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="flex gap-6 border-b border-border pb-8">
                  <span className="font-mono-accent text-sm text-emerald pt-1">{c.n}</span>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-foreground">{c.t}</h3>
                    <p className="mt-2 text-muted-foreground leading-relaxed">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <Reveal><Overline>What We Offer</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="mt-4 font-heading text-4xl lg:text-5xl font-extrabold text-foreground">Explore our categories</h2></Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/products" className="inline-flex items-center gap-2 font-semibold text-royal hover:gap-3 transition-all">View all products <ArrowRight size={18} /></Link>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 0.08}>
              <Link to={`/products?category=${c.slug}`} data-testid={`cat-card-${c.slug}`} className="group block rounded-4xl border border-border bg-card overflow-hidden hover:shadow-2xl hover:shadow-royal/5 hover:-translate-y-1 transition-all duration-300">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={c.image} alt={c.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <span className="absolute top-4 left-4 rounded-full glass px-3 py-1 text-xs font-mono-accent uppercase tracking-wider text-foreground">{c.tagline}</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-xl font-bold text-foreground group-hover:text-royal transition-colors">{c.name}</h3>
                    <ArrowUpRight size={20} className="text-muted-foreground group-hover:text-royal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{c.short}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE — bento */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 lg:py-32">
        <Reveal><Overline>Why ElitzPacks</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="mt-4 font-heading text-4xl lg:text-5xl font-extrabold text-foreground max-w-2xl">Packaging built around your business.</h2></Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY_CHOOSE.map((w, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <Reveal key={w.title} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-3xl border border-border bg-card p-8 hover:border-royal/40 transition-colors group">
                  <span className="grid place-items-center h-12 w-12 rounded-2xl bg-secondary text-royal group-hover:bg-royal group-hover:text-white transition-colors">
                    <Icon size={22} />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-foreground">{w.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-navy text-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <span className="overline text-emerald">Industries We Serve</span>
              <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-extrabold">Trusted across every sector.</h2>
            </div>
            <p className="max-w-sm text-white/60">From food and retail to industrial and e-commerce, our packaging is engineered for your industry&rsquo;s demands.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind} delay={(i % 6) * 0.04}>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium hover:bg-white hover:text-navy transition-colors cursor-default">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald" />{ind}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 lg:py-32">
        <Reveal><Overline>How We Work</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="mt-4 font-heading text-4xl lg:text-5xl font-extrabold text-foreground">From idea to delivery.</h2></Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={(i % 3) * 0.08}>
              <div className="border-t border-border pt-6">
                <span className="font-mono-accent text-3xl font-semibold text-emerald">{p.n}</span>
                <h3 className="mt-4 font-heading text-xl font-bold text-foreground">{p.title}</h3>
                <p className="mt-2 text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10">
        <Reveal><Overline>Gallery</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="mt-4 font-heading text-4xl lg:text-5xl font-extrabold text-foreground mb-12">Inside our world.</h2></Reveal>
        <div className="columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {GALLERY.map((src, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06} className="mb-5 break-inside-avoid">
              <div className="overflow-hidden rounded-3xl border border-border group">
                <img src={src} alt={`Gallery ${i + 1}`} loading="lazy" className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${i % 3 === 0 ? "aspect-[3/4]" : "aspect-[4/3]"}`} />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 lg:py-32">
        <Reveal><Overline>Happy Clients</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="mt-4 font-heading text-4xl lg:text-5xl font-extrabold text-foreground mb-12">Loved by growing brands.</h2></Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={(i % 3) * 0.08}>
              <div className="h-full rounded-3xl border border-border bg-card p-8 flex flex-col">
                <div className="flex gap-1 text-emerald mb-4">{Array.from({ length: t.rating }).map((_, s) => <Star key={s} size={16} fill="currentColor" />)}</div>
                <p className="text-foreground leading-relaxed flex-1">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-6 pt-5 border-t border-border">
                  <p className="font-heading font-bold text-foreground">{t.name}</p>
                  <p className="text-sm text-muted-foreground">{t.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 pb-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-4xl bg-navy text-white px-8 py-16 lg:p-20">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-royal/30 blur-3xl" />
            <div className="absolute -left-10 bottom-0 h-56 w-56 rounded-full bg-emerald/20 blur-3xl" />
            <div className="relative z-10 max-w-2xl">
              <span className="overline text-emerald">Ready to package your products?</span>
              <h2 className="mt-4 font-heading text-4xl lg:text-6xl font-extrabold leading-[1.02]">Let&rsquo;s build the right packaging solution.</h2>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/request-quote" className="inline-flex items-center gap-2 rounded-full bg-white text-navy px-7 py-3.5 font-semibold hover:bg-emerald hover:text-white transition-colors">Request a Quote <ArrowUpRight size={18} /></Link>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-semibold hover:bg-white/10 transition-colors">Contact Us</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
